import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Inbox, RefreshCw, RotateCcw, Send } from 'lucide-react';
import { toast } from 'sonner';

// Postausgang einer Mail-Art (seit 22.10.2026, api/_lib/mailAuftragPostausgang.js): was wartet,
// was wiederholt wird, was endgueltig gescheitert ist - mit "Erneut senden" je Fehlschlag und
// "Für Deal senden" fuer Ausfaelle aus der Zeit davor (etwa Deal 12118 und 12206).

const GRUPPEN = [
  { schluessel: 'fehlgeschlagen', titel: 'Endgültig fehlgeschlagen', farbe: '#B91C1C', grund: '#FEF2F2', rand: '#FECACA' },
  { schluessel: 'wiederholung', titel: 'Wird wiederholt', farbe: '#B45309', grund: '#FFFBEB', rand: '#FDE68A' },
  { schluessel: 'wartend', titel: 'Wartet auf Versand', farbe: '#475569', grund: '#F8FAFC', rand: '#E2E8F0' },
];

const NACH_AKTION_AKTUALISIEREN_MS = 6000;

function zeitpunkt(wert) {
  if (!wert) return '';
  const datum = new Date(wert);
  if (Number.isNaN(datum.getTime())) return '';
  return datum.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function zeileText(eintrag, schluessel) {
  const versuche = eintrag.versuche === 1 ? '1 Versuch' : `${eintrag.versuche} Versuche`;
  if (schluessel === 'fehlgeschlagen') return `${versuche} · aufgegeben ${zeitpunkt(eintrag.fehlgeschlagenAm)}`;
  if (schluessel === 'wiederholung') return `${versuche} · nächster Versuch ${zeitpunkt(eintrag.naechsterVersuchAm)}`;
  return eintrag.inArbeit ? 'wird gerade versendet' : `seit ${zeitpunkt(eintrag.erstelltAm)}`;
}

function KleinerKnopf({ children, onClick, disabled, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: '#334155',
        border: '1px solid #E2E8F0', borderRadius: 7, padding: '6px 11px', fontSize: 12, fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
      }}
    >
      {Icon && <Icon size={13} />}
      {children}
    </button>
  );
}

export default function MailPostausgang({ apiBase, authHeaders, einstellungsPfad, postausgang, onPostausgang }) {
  const [dealEingabe, setDealEingabe] = useState('');
  const [beschaeftigt, setBeschaeftigt] = useState('');
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const aktualisieren = useCallback(async () => {
    try {
      const antwort = await axios.get(`${apiBase}/automation/${einstellungsPfad}`, { headers: authHeaders });
      onPostausgang(antwort.data?.postausgang || null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Postausgang konnte nicht geladen werden.');
    }
  }, [apiBase, authHeaders, einstellungsPfad, onPostausgang]);

  const senden = async (aktion, dealId) => {
    setBeschaeftigt(`${aktion}:${dealId}`);
    try {
      const antwort = await axios.post(`${apiBase}/automation/${einstellungsPfad}`, { aktion, dealId }, { headers: authHeaders });
      toast.success(antwort.data?.message || 'In den Postausgang gelegt.');
      if (antwort.data?.postausgang) onPostausgang(antwort.data.postausgang);
      if (aktion === 'fuer-deal-senden') setDealEingabe('');
      // Versendet wird im Hintergrund - einmal nachsehen, wie es ausging.
      clearTimeout(timer.current);
      timer.current = setTimeout(aktualisieren, NACH_AKTION_AKTUALISIEREN_MS);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Auftrag konnte nicht eingereiht werden.');
    } finally {
      setBeschaeftigt('');
    }
  };

  const dealGueltig = /^\d{1,12}$/.test(dealEingabe.trim());
  const leer = postausgang && !postausgang.fehler
    && GRUPPEN.every(({ schluessel }) => !(postausgang[schluessel] || []).length);

  return (
    <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, padding: '14px 16px', display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <Inbox size={13} />
          Postausgang
        </div>
        <KleinerKnopf onClick={aktualisieren} icon={RefreshCw}>Aktualisieren</KleinerKnopf>
      </div>

      {postausgang?.fehler && <div style={{ fontSize: 12, color: '#B91C1C' }}>{postausgang.fehler}</div>}
      {leer && <div style={{ fontSize: 12, color: '#64748B' }}>Nichts offen – alle Aufträge sind versendet oder erledigt.</div>}
      {postausgang?.unlesbar > 0 && (
        <div style={{ fontSize: 12, color: '#B45309' }}>
          {postausgang.unlesbar} Auftrag/Aufträge gerade nicht lesbar – sie bleiben liegen und werden später versucht.
        </div>
      )}

      {GRUPPEN.map(({ schluessel, titel, farbe, grund, rand }) => {
        const eintraege = postausgang?.[schluessel] || [];
        if (!eintraege.length) return null;
        return (
          <div key={schluessel} style={{ display: 'grid', gap: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: farbe }}>{titel} ({eintraege.length})</div>
            {eintraege.map((eintrag) => (
              <div
                key={`${schluessel}-${eintrag.dealId}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: grund,
                  border: `1px solid ${rand}`, borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#334155',
                }}
              >
                <strong style={{ color: '#0F172A' }}>Deal {eintrag.dealId}</strong>
                <span>{zeileText(eintrag, schluessel)}</span>
                {eintrag.grund && <span style={{ color: '#64748B', flex: '1 1 220px' }}>{eintrag.grund}</span>}
                {schluessel === 'fehlgeschlagen' && (
                  <KleinerKnopf
                    onClick={() => senden('erneut-senden', eintrag.dealId)}
                    disabled={Boolean(beschaeftigt)}
                    icon={RotateCcw}
                  >
                    {beschaeftigt === `erneut-senden:${eintrag.dealId}` ? 'Reiht ein…' : 'Erneut senden'}
                  </KleinerKnopf>
                )}
              </div>
            ))}
          </div>
        );
      })}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap', borderTop: '1px solid #F1F5F9', paddingTop: 12 }}>
        <div style={{ width: 170 }}>
          <label htmlFor={`${einstellungsPfad}-fuer-deal`} style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 5 }}>
            Für Deal senden
          </label>
          <input
            id={`${einstellungsPfad}-fuer-deal`}
            type="text"
            inputMode="numeric"
            value={dealEingabe}
            onChange={(e) => setDealEingabe(e.target.value.replace(/\D/g, ''))}
            placeholder="z. B. 12118"
            style={{
              width: '100%', border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 11px', fontSize: 13,
              color: '#0F172A', background: '#fff', fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
        </div>
        <KleinerKnopf
          onClick={() => senden('fuer-deal-senden', dealEingabe.trim())}
          disabled={!dealGueltig || Boolean(beschaeftigt)}
          icon={Send}
        >
          {beschaeftigt.startsWith('fuer-deal-senden') ? 'Reiht ein…' : 'In den Postausgang'}
        </KleinerKnopf>
      </div>
      <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>
        Regulärer Auftrag an die hinterlegten Empfänger, mit Doppelschutz – etwa für Deals, deren Mail vor dem
        Postausgang gescheitert ist. Versendet wird eine Mail nach der anderen; scheitert der Versand vorübergehend,
        wird wiederholt – höchstens sechs Versuche, Abstände 20 s bis 30 min (drosselt der Mailserver, bis 2 h).
        Erst danach steht ein Fehlervermerk im Deal.
      </p>
    </div>
  );
}
